import { useEffect, useMemo, useRef, useState } from 'react';
import { ucitajExcel, preuzmiPredlozak } from '../lib/ucitajExcel';
import {
  POLJA_UVOZA,
  prepoznajZaglavlje,
  prepoznajStupce,
  provjeriMapiranje,
  pripremiUvoz,
  provjeriOdabraniUvoz,
} from '../lib/uvozExcel';
import { pronadjiDuplikat, provjeriKucanstvo } from '../lib/unosKucanstva';
import PoljaKucanstva from './PoljaKucanstva';
import Ikona from './Ikona';

function DijalogPregleda({ grupa, kucanstva, naZatvori, naPotvrdu }) {
  const dijalog = useRef(null);
  const [podaci, postaviPodatke] = useState({ ...grupa.podaci });
  const [kontakti, postaviKontakte] = useState(
    grupa.kontakti.map((k) => ({ ...k })),
  );
  const [greske, postaviGreske] = useState([]);
  useEffect(() => {
    const d = dijalog.current;
    d.showModal();
    return () => d.close();
  }, []);
  const potvrdi = () => {
    const poruke = provjeriKucanstvo(podaci, kontakti);
    if (pronadjiDuplikat(podaci, kucanstva))
      poruke.push(
        'Ova adresa već postoji. Postojeći zapis neće se prepisivati.',
      );
    if (poruke.length) {
      postaviGreske(poruke);
      return;
    }
    naPotvrdu({
      ...grupa,
      podaci,
      kontakti,
      greske: [],
      pregledano: true,
      odabrano: true,
    });
  };
  return (
    <dialog
      ref={dijalog}
      className="dijalog-uvoza"
      onCancel={(e) => {
        e.preventDefault();
        naZatvori();
      }}
      aria-labelledby="naslov-pregleda-uvoza"
    >
      <div className="vrh-dijaloga">
        <div>
          <span className="nadnaslov">REDCI {grupa.redci.join(', ')}</span>
          <h2 id="naslov-pregleda-uvoza">Pregledaj kućanstvo</h2>
        </div>
        <button
          className="gumb-ikona"
          aria-label="Zatvori pregled uvoza"
          onClick={naZatvori}
        >
          <Ikona naziv="zatvori" />
        </button>
      </div>
      <div className="sadrzaj-dijaloga">
        {grupa.nositelji.length > 1 && (
          <div className="obavijest uvoz-upozorenje">
            <strong>U datoteci su različiti nositelji</strong>
            <p>{grupa.nositelji.join(' · ')}</p>
            <p>
              Upiši ispravno ime i prezime. Kontakti iz redaka ostaju za
              pregled.
            </p>
          </div>
        )}
        {grupa.upozorenja.length > 0 && (
          <div className="obavijest uvoz-upozorenje">
            <ul>
              {grupa.upozorenja.map((u) => (
                <li key={u}>{u}</li>
              ))}
            </ul>
          </div>
        )}
        {grupa.napomene.length > 0 && (
          <section className="izvorne-napomene">
            <h3>Napomene iz datoteke</h3>
            <p>
              Pregledaj sadrže li podatke drugih osoba. U zapis uključi samo ono
              što pripada ovom kućanstvu.
            </p>
            {grupa.napomene.map((n) => (
              <blockquote key={n.redak}>
                <small>Redak {n.redak}</small>
                {n.tekst}
              </blockquote>
            ))}
            <button
              className="tekst-gumb"
              onClick={() =>
                postaviPodatke({
                  ...podaci,
                  napomena: grupa.napomene.map((n) => n.tekst).join('\n'),
                })
              }
            >
              Preuzmi napomene u polje za uređivanje
            </button>
          </section>
        )}
        {greske.length > 0 && (
          <div className="obavijest greska" role="alert">
            <ul>
              {greske.map((g) => (
                <li key={g}>{g}</li>
              ))}
            </ul>
          </div>
        )}
        <PoljaKucanstva
          podaci={podaci}
          kontakti={kontakti}
          naPodatke={postaviPodatke}
          naKontakte={postaviKontakte}
        />
        <p className="pomoc-polja">
          Potvrdom pregleda prihvaćaš prikazane vrijednosti. Koordinate iz
          datoteke ostaju nepotvrđene.
        </p>
      </div>
      <div className="radnje-obrasca">
        <button className="gumb sek" onClick={naZatvori}>
          Odustani
        </button>
        <button className="gumb" onClick={potvrdi}>
          Potvrdi pregled <Ikona naziv="potvrda" velicina={17} />
        </button>
      </div>
    </dialog>
  );
}

export default function UvozKucanstava({
  kucanstva,
  ucitavaEvidenciju,
  naUvoz,
  naPopis,
  naPostojece,
}) {
  const [korak, postaviKorak] = useState(1);
  const [listovi, postaviListove] = useState([]);
  const [list, postaviList] = useState(0);
  const [redZaglavlja, postaviRedZaglavlja] = useState(0);
  const [stupci, postaviStupce] = useState({});
  const [redoslijed, postaviRedoslijed] = useState('ime-prezime');
  const [naziv, postaviNaziv] = useState('');
  const [grupe, postaviGrupe] = useState([]);
  const [uredjuje, postaviUredjuje] = useState(null);
  const [filter, postaviFilter] = useState('svi');
  const [radi, postaviRadi] = useState(false);
  const [greska, postaviGresku] = useState(null);
  const [rezultat, postaviRezultat] = useState(null);
  const zahtjev = useRef(0);
  const zakljucano = useRef(false);
  useEffect(
    () => () => {
      zahtjev.current++;
    },
    [],
  );
  const odaberiList = (indeks, dostupni = listovi) => {
    const prepoznato = prepoznajZaglavlje(dostupni[indeks].redci);
    postaviList(indeks);
    postaviRedZaglavlja(prepoznato.red);
    postaviStupce(prepoznato.stupci);
  };
  const ucitaj = async (datoteka) => {
    if (!datoteka) return;
    const oznaka = ++zahtjev.current;
    postaviRadi(true);
    postaviGresku(null);
    postaviGrupe([]);
    postaviRezultat(null);
    try {
      const dostupni = await ucitajExcel(datoteka);
      if (oznaka !== zahtjev.current) return;
      if (!dostupni.length) throw new Error('Datoteka nema radnih listova.');
      let odabrani = 0;
      dostupni.forEach((l, i) => {
        if (
          prepoznajZaglavlje(l.redci).bodovi >
          prepoznajZaglavlje(dostupni[odabrani].redci).bodovi
        )
          odabrani = i;
      });
      postaviListove(dostupni);
      postaviNaziv(datoteka.name);
      odaberiList(odabrani, dostupni);
      postaviKorak(2);
    } catch (e) {
      if (oznaka === zahtjev.current) postaviGresku(e.message);
    } finally {
      if (oznaka === zahtjev.current) postaviRadi(false);
    }
  };
  const pripremi = () => {
    const poruke = provjeriMapiranje(stupci);
    if (poruke.length) {
      postaviGresku(poruke.join(' '));
      return;
    }
    const pripremljene = pripremiUvoz(
      listovi[list].redci,
      redZaglavlja,
      stupci,
      kucanstva,
      redoslijed,
    ).map((g) => ({ ...g, zahtjev: crypto.randomUUID() }));
    if (!pripremljene.length) {
      postaviGresku('Ispod odabranog zaglavlja nema podataka za uvoz.');
      return;
    }
    postaviGrupe(pripremljene);
    postaviGresku(null);
    postaviKorak(3);
    postaviFilter('svi');
  };
  const stanje = (g) => {
    const postojece = pronadjiDuplikat(g.podaci, kucanstva);
    if (postojece) return { vrsta: 'postoji', naziv: 'Već postoji', postojece };
    if (g.greske.length || (g.upozorenja.length && !g.pregledano))
      return { vrsta: 'provjera', naziv: 'Za pregled' };
    return {
      vrsta: 'novo',
      naziv: g.pregledano ? 'Pregledano' : 'Novo kućanstvo',
    };
  };
  const brojaci = useMemo(
    () => ({
      ukupno: grupe.length,
      postoji: grupe.filter((g) => pronadjiDuplikat(g.podaci, kucanstva))
        .length,
      provjera: grupe.filter(
        (g) =>
          !pronadjiDuplikat(g.podaci, kucanstva) &&
          (g.greske.length || (g.upozorenja.length && !g.pregledano)),
      ).length,
    }),
    [grupe, kucanstva],
  );
  const odabrane = grupe.filter(
    (g) => g.odabrano && stanje(g).vrsta === 'novo',
  );
  const prikazane = grupe.filter(
    (g) => filter === 'svi' || stanje(g).vrsta === filter,
  );
  const spremi = async () => {
    if (zakljucano.current || !odabrane.length || odabrane.length > 500) return;
    const provjere = provjeriOdabraniUvoz(odabrane);
    if (provjere.length) {
      postaviGresku(provjere.join(' '));
      return;
    }
    zakljucano.current = true;
    postaviRadi(true);
    postaviGresku(null);
    try {
      postaviRezultat(await naUvoz(odabrane));
      postaviKorak(4);
    } catch (e) {
      postaviGresku(
        `${e.message} Ako je veza prekinuta, pokušaj ponovno s istim odabirom. Isti zahtjev neće stvoriti duplikate.`,
      );
    } finally {
      zakljucano.current = false;
      postaviRadi(false);
    }
  };
  return (
    <div className="uvoz-kucanstava">
      <ol className="koraci-uvoza" aria-label="Koraci uvoza">
        {['Odaberi datoteku', 'Provjeri stupce', 'Pregledaj i spremi'].map(
          (k, i) => (
            <li
              key={k}
              className={
                korak === i + 1 || (i === 2 && korak === 4)
                  ? 'aktivan'
                  : korak > i + 1
                    ? 'gotov'
                    : ''
              }
            >
              <span>{korak > i + 1 ? '✓' : i + 1}</span>
              {k}
            </li>
          ),
        )}
      </ol>
      {greska && (
        <p className="obavijest greska" role="alert">
          {greska}
        </p>
      )}
      {korak === 1 && (
        <section className="ploca odabir-datoteke">
          <span className="ikona-ploha tirkiz">
            <Ikona naziv="knjiga" velicina={28} />
          </span>
          <h2>Dodaj kućanstva iz Excela</h2>
          <p>
            Odaberi postojeću evidenciju ili popuni naš predložak.
            <br />
            Prije spremanja vidjet ćeš svaki novi zapis i sve nejasnoće.
          </p>
          <label className={`gumb ${radi ? 'onemoguceno' : ''}`}>
            {radi ? 'Čitam datoteku…' : 'Odaberi Excel datoteku'}
            <input
              type="file"
              accept=".xlsx,.xls"
              className="samo-citac"
              disabled={radi}
              onChange={(e) => ucitaj(e.target.files?.[0])}
              aria-label="Excel datoteka za uvoz"
            />
          </label>
          <button
            className="tekst-gumb"
            disabled={radi}
            onClick={async () => {
              postaviRadi(true);
              try {
                await preuzmiPredlozak();
              } catch (e) {
                postaviGresku(e.message);
              } finally {
                postaviRadi(false);
              }
            }}
          >
            Preuzmi prazan predložak
          </button>
          <small>.xlsx ili .xls · do 5 MB · do 2000 redaka</small>
          <p className="privatnost-uvoza">
            <Ikona naziv="lokot" velicina={15} />
            Datoteka se obrađuje na tvom uređaju. U bazu idu samo odabrani
            zapisi.
          </p>
        </section>
      )}
      {korak === 2 && (
        <section className="ploca mapiranje-stupaca">
          <div className="naslov-ploce">
            <div>
              <h2>Prepoznali smo stupce</h2>
              <p>{naziv} · provjeri jesu li polja ispravno povezana.</p>
            </div>
          </div>
          <div className="postavke-lista">
            <label>
              Radni list
              <select
                value={list}
                onChange={(e) => odaberiList(Number(e.target.value))}
              >
                {listovi.map((l, i) => (
                  <option key={i} value={i}>
                    {l.naziv}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Redak sa zaglavljem
              <input
                type="number"
                min="1"
                max={Math.max(1, Math.min(30, listovi[list].redci.length))}
                value={redZaglavlja + 1}
                onChange={(e) => {
                  const red = Number(e.target.value) - 1;
                  if (
                    red >= 0 &&
                    red < Math.min(30, listovi[list].redci.length)
                  ) {
                    postaviRedZaglavlja(red);
                    postaviStupce(prepoznajStupce(listovi[list].redci[red]));
                  }
                }}
              />
            </label>
            <label>
              Redoslijed punog imena
              <select
                value={redoslijed}
                onChange={(e) => postaviRedoslijed(e.target.value)}
              >
                <option value="ime-prezime">Ime pa prezime</option>
                <option value="prezime-ime">Prezime pa ime</option>
              </select>
            </label>
          </div>
          <p className="pomoc-mapiranja">
            Za nositelja koristi puno ime ili zasebne stupce. Za adresu koristi
            punu adresu ili naselje i kućni broj. Ostala polja su neobavezna.
          </p>
          <div className="mreza-mapiranja">
            {POLJA_UVOZA.map((p) => (
              <label key={p.kljuc}>
                {p.naziv}
                <select
                  value={stupci[p.kljuc] ?? -1}
                  onChange={(e) =>
                    postaviStupce({
                      ...stupci,
                      [p.kljuc]: Number(e.target.value),
                    })
                  }
                >
                  <option value={-1}>Ne preuzimaj</option>
                  {(listovi[list].redci[redZaglavlja] ?? []).map((z, i) => (
                    <option key={i} value={i}>
                      {i + 1}. {z || 'Bez naziva'}
                    </option>
                  ))}
                </select>
                {stupci[p.kljuc] >= 0 && (
                  <small>
                    Primjer:{' '}
                    {listovi[list].redci
                      .slice(redZaglavlja + 1)
                      .find((r) => r[stupci[p.kljuc]])?.[stupci[p.kljuc]] ||
                      '—'}
                  </small>
                )}
              </label>
            ))}
          </div>
          <div className="radnje-obrasca">
            <button
              className="gumb sek"
              onClick={() => {
                postaviKorak(1);
                postaviGresku(null);
              }}
            >
              Druga datoteka
            </button>
            <button
              className="gumb"
              onClick={pripremi}
              disabled={ucitavaEvidenciju}
            >
              Pregledaj zapise <Ikona naziv="strelica" velicina={17} />
            </button>
          </div>
        </section>
      )}
      {korak === 3 && (
        <>
          <div className="sazetak-uvoza">
            <div>
              <strong>{brojaci.ukupno}</strong>
              <span>
                kućanstava iz {grupe.reduce((z, g) => z + g.redci.length, 0)}{' '}
                redaka
              </span>
            </div>
            <div>
              <strong>{odabrane.length}</strong>
              <span>odabrano za uvoz</span>
            </div>
            <div>
              <strong>{brojaci.postoji}</strong>
              <span>već u evidenciji</span>
            </div>
            <div>
              <strong>{brojaci.provjera}</strong>
              <span>traži pregled</span>
            </div>
          </div>
          <section className="ploca pregled-uvoza">
            <div className="zaglavlje-uvoza">
              <div>
                <h2>Pregled prije spremanja</h2>
                <p>
                  Postojeća kućanstva se preskaču. Nejasne zapise pregledaj ili
                  ostavi za kasnije.
                </p>
              </div>
              <button
                className="tekst-gumb"
                disabled={radi}
                onClick={() => {
                  postaviKorak(2);
                  postaviGresku(null);
                }}
              >
                Promijeni stupce
              </button>
            </div>
            <div className="filteri">
              {[
                ['svi', 'Svi zapisi'],
                ['novo', 'Spremni'],
                ['provjera', 'Za pregled'],
                ['postoji', 'Već postoje'],
              ].map(([v, n]) => (
                <button
                  key={v}
                  aria-pressed={filter === v}
                  onClick={() => postaviFilter(v)}
                  disabled={radi}
                >
                  {n}
                </button>
              ))}
            </div>
            <div className="odabir-uvoza">
              <label className="redak-kvacice">
                <input
                  type="checkbox"
                  checked={
                    odabrane.length > 0 &&
                    odabrane.length ===
                      grupe.filter((g) => stanje(g).vrsta === 'novo').length
                  }
                  disabled={radi}
                  onChange={(e) =>
                    postaviGrupe(
                      grupe.map((g) => ({
                        ...g,
                        odabrano:
                          e.target.checked && stanje(g).vrsta === 'novo',
                      })),
                    )
                  }
                />
                Odaberi sve spremne zapise
              </label>
              <span>{naziv}</span>
            </div>
            <div className="tablica-uvoza">
              <table>
                <thead>
                  <tr>
                    <th>Odabir</th>
                    <th>Kućanstvo / redci</th>
                    <th>Nositelj</th>
                    <th>Kontakti</th>
                    <th>Provjera</th>
                    <th>Radnja</th>
                  </tr>
                </thead>
                <tbody>
                  {prikazane.map((g) => {
                    const s = stanje(g);
                    return (
                      <tr key={g.oznaka}>
                        <td>
                          <input
                            type="checkbox"
                            aria-label={`Odaberi ${g.podaci.naselje} ${g.podaci.kucni_broj}`}
                            checked={g.odabrano && s.vrsta === 'novo'}
                            disabled={radi || s.vrsta !== 'novo'}
                            onChange={(e) =>
                              postaviGrupe(
                                grupe.map((v) =>
                                  v.oznaka === g.oznaka
                                    ? { ...v, odabrano: e.target.checked }
                                    : v,
                                ),
                              )
                            }
                          />
                        </td>
                        <td>
                          <strong>
                            {g.podaci.naselje || 'Naselje nedostaje'}{' '}
                            {g.podaci.kucni_broj || 'bb'}
                          </strong>
                          <small>Redci {g.redci.join(', ')}</small>
                          {g.redci.length > 1 && (
                            <small>Više redaka · jedno kućanstvo</small>
                          )}
                        </td>
                        <td>
                          {g.podaci.nositelj_ime || '—'}{' '}
                          {g.podaci.nositelj_prezime}
                        </td>
                        <td>{g.kontakti.length}</td>
                        <td>
                          <span className={`oznaka-uvoza ${s.vrsta}`}>
                            {s.naziv}
                          </span>
                          {s.vrsta === 'provjera' && (
                            <small>{g.greske[0] || g.upozorenja[0]}</small>
                          )}
                        </td>
                        <td>
                          {s.postojece ? (
                            <button
                              className="tekst-gumb"
                              disabled={radi}
                              onClick={() => naPostojece(s.postojece.id)}
                            >
                              Otvori postojeće
                            </button>
                          ) : (
                            <button
                              className="tekst-gumb"
                              disabled={radi}
                              onClick={() => postaviUredjuje(g)}
                            >
                              Pregledaj / uredi
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {!prikazane.length && (
                <p className="prazno">Nema zapisa u odabranom prikazu.</p>
              )}
            </div>
            <div className="radnje-obrasca">
              <p className="pomoc-polja">
                Spremaju se samo označena kućanstva i njihovi kontakti, zajedno.{' '}
                {odabrane.length > 500
                  ? 'Odaberi najviše 500 kućanstava odjednom.'
                  : ''}
              </p>
              <button
                className="gumb"
                onClick={spremi}
                disabled={
                  radi ||
                  ucitavaEvidenciju ||
                  !odabrane.length ||
                  odabrane.length > 500
                }
              >
                {radi
                  ? 'Spremam odabrane zapise…'
                  : `Uvezi odabrana kućanstva (${odabrane.length})`}
                <Ikona naziv="potvrda" velicina={17} />
              </button>
            </div>
          </section>
        </>
      )}
      {korak === 4 && rezultat && (
        <section className="ploca uspjeh-uvoza">
          <span className="ikona-ploha tirkiz">
            <Ikona naziv="potvrda" velicina={30} />
          </span>
          <h2>Uvoz je dovršen</h2>
          <p>
            Novih kućanstava: <strong>{rezultat.novih}</strong>.{' '}
            {rezultat.ponovljenih > 0
              ? `${rezultat.ponovljenih} zapisa već je spremljeno istim zahtjevom.`
              : 'Njihovi kontakti spremljeni su u istoj transakciji.'}
          </p>
          <p>Postojeći zapisi i potvrđene lokacije ostali su sačuvani.</p>
          {!rezultat.osvjezeno && (
            <p className="obavijest uvoz-upozorenje">
              Podaci su spremljeni, ali prikaz treba ponovno osvježiti.
            </p>
          )}
          <div>
            <button className="gumb" onClick={naPopis}>
              Otvori kućanstva <Ikona naziv="strelica" velicina={17} />
            </button>
            <button
              className="gumb sek"
              onClick={() => {
                postaviKorak(1);
                postaviGrupe([]);
                postaviListove([]);
                postaviRezultat(null);
              }}
            >
              Uvezi drugu datoteku
            </button>
          </div>
        </section>
      )}
      {uredjuje && (
        <DijalogPregleda
          key={uredjuje.oznaka}
          grupa={uredjuje}
          kucanstva={kucanstva}
          naZatvori={() => postaviUredjuje(null)}
          naPotvrdu={(g) => {
            postaviGrupe(grupe.map((v) => (v.oznaka === g.oznaka ? g : v)));
            postaviUredjuje(null);
          }}
        />
      )}
    </div>
  );
}
